import { changeOrganizationSession } from "./orgSwitchService";
import {
  getLoginModuleRows,
  getSessionOrganizationId,
  loginSessionHasModulesProperty,
  normalizeUserSessionModules,
} from "../utils/loginModulesUtils";
import { isSessionSuperAdmin } from "../utils/permissionUtils";
import { isSponsorCroSession } from "../utils/sponsorCroAccess";

const HYDRATION_ATTEMPT_KEY = "aq_orgModulesHydrationAttempt";

function hydrationAttemptKey(userData) {
  const orgId = getSessionOrganizationId(userData);
  const staffId = userData?.StaffId || "";
  return `${staffId}|${orgId}`;
}

function markHydrationAttempted(userData) {
  try {
    sessionStorage.setItem(
      HYDRATION_ATTEMPT_KEY,
      hydrationAttemptKey(userData),
    );
  } catch {
    // ignore private mode / quota
  }
}

function hasHydrationBeenAttempted(userData) {
  try {
    return (
      sessionStorage.getItem(HYDRATION_ATTEMPT_KEY) ===
      hydrationAttemptKey(userData)
    );
  } catch {
    return false;
  }
}

function deriveOrgType(userData) {
  const orgId = getSessionOrganizationId(userData);
  const staffId = userData?.StaffId || "";
  const links = userData?.OtherOrgLink;
  if (Array.isArray(links)) {
    const match = links.find(
      (row) =>
        String(row?.OrganizationId || row?.organizationId || "") ===
          String(orgId || "") &&
        String(row?.StaffId || row?.staffId || "") === String(staffId),
    );
    if (match?.OrgType) return match.OrgType;
    if (match?.orgType) return match.orgType;
  }
  return Number(userData?.IsCRO) === 1 || Number(userData?.IsCRO) === 2
    ? "CRO"
    : "Site";
}

/** True when session has no usable licensed-module list yet. */
export function shouldHydrateOrgModules(userData) {
  if (!userData || !getSessionOrganizationId(userData) || !userData?.StaffId) {
    return false;
  }
  /* AQ platform Super Admin — the AQ org has no licensed modules; calling
     ChangeOrganization would rotate/delete the login session and force logout. */
  if (isSessionSuperAdmin(userData)) return false;
  if (isSponsorCroSession(userData)) return false;
  if (hasHydrationBeenAttempted(userData)) return false;

  /* MFA-on orgs: ChangeOrganization without a fresh MFA token returns MFA_REQUIRED
     or can rotate the session and 401 the client. Login/org-switch already
     return Modules — skip this refresh path. */
  const mfaFlag = userData.MfaEnabled ?? userData.mfaEnabled;
  if (mfaFlag !== false && mfaFlag !== 0 && mfaFlag !== "0") {
    return false;
  }

  const rows = getLoginModuleRows(userData);
  if (rows === null) return true;
  if (!loginSessionHasModulesProperty(userData)) return true;
  return rows.length === 0;
}

/**
 * Refresh `userData.Modules` from ChangeOrganization when login did not store them.
 * Uses the current organisation (no UI switch).
 */
export async function hydrateOrgModulesIfMissing(userData) {
  if (!shouldHydrateOrgModules(userData)) {
    return normalizeUserSessionModules({ ...userData });
  }

  markHydrationAttempted(userData);

  try {
    const response = await changeOrganizationSession(userData, {
      organizationId: getSessionOrganizationId(userData),
      staffId: userData.StaffId,
      orgType: deriveOrgType(userData),
    });

    if (Number(response?.success) !== 1 || !response?.data) {
      return normalizeUserSessionModules({ ...userData });
    }

    const session = response.data;
    const merged = normalizeUserSessionModules({
      ...userData,
      ...session,
      // ChangeOrganization issues a fresh session key; keep both fields in sync
      // so getAuthHeaders never falls back to the old (deleted) key.
      Sessionkey: session.Sessionkey || session.APIPWD || userData.Sessionkey,
      APIPWD: session.APIPWD || session.Sessionkey || userData.APIPWD,
      OrganizationId:
        session.OrganizationId ||
        session.Organizationid ||
        getSessionOrganizationId(userData),
      Modules:
        session.Modules !== undefined
          ? session.Modules
          : session.modules !== undefined
            ? session.modules
            : userData.Modules,
    });

    return merged;
  } catch {
    return normalizeUserSessionModules({ ...userData });
  }
}

export function clearOrgModulesHydrationAttempt() {
  try {
    sessionStorage.removeItem(HYDRATION_ATTEMPT_KEY);
  } catch {
    // ignore
  }
}
